import React, { useState } from 'react';
import { Box, Container, Typography, Divider, Grid } from '@mui/material';

/**
 * GENESIS V2 - CYCLE 3 - SEED A
 * CONCEPT: "STRUCTURAL CAPABILITY" (Layout-First)
 * PERSONA: The Architect / Engineer
 * MOOD: Rational, Schematic, Blueprint, "CAD Drawing"
 */

const TechLabel = ({ children, color = '#666' }) => (
    <Typography
        variant="caption"
        sx={{
            fontFamily: '"JetBrains Mono", monospace',
            fontSize: '0.65rem',
            color: color,
            letterSpacing: '0.1em',
            textTransform: 'uppercase',
            display: 'block',
            mb: 0.5
        }}
    >
        {children}
    </Typography>
);

const GenesisV2_Intel_SeedA = () => {
    // theme currently unused; keep hook import for future palette wiring
    // const theme = useTheme();
    const [hoveredZone, setHoveredZone] = useState(null);

    // Schematic Lines Component
    const SchematicLine = ({ active }) => (
        <Box sx={{
            height: '1px',
            width: '100%',
            bgcolor: active ? '#CC0000' : '#e0e0e0',
            my: 'clamp(10px, 1.6vmin, 16px)',
            transition: 'all 0.3s ease',
            position: 'relative',
            '&::after': {
                content: '""',
                position: 'absolute',
                right: 0,
                top: '-2px',
                width: '5px',
                height: '5px',
                bgcolor: active ? '#CC0000' : '#e0e0e0',
                borderRadius: '50%'
            }
        }} />
    );

    return (
        <Box sx={{
            bgcolor: '#f4f4f4', // Technical Engineering Paper Color
            // Background Grid Pattern
            backgroundImage: `
                linear-gradient(#e0e0e0 1px, transparent 1px),
                linear-gradient(90deg, #e0e0e0 1px, transparent 1px)
            `,
            backgroundSize: '40px 40px',
            color: '#050505',
            height: '100%', // Fill SectionWrapper
            // Let SectionWrapper manage scrolling; this component should fit by scaling typography.
            overflow: 'hidden',
            // Keep padding lightweight on short viewports to prevent top clipping.
            py: 'clamp(10px, 2vmin, 24px)',
            position: 'relative',
        }}>
            <Container
                maxWidth={false}
                sx={{
                    maxWidth: '1800px',
                    px: { xs: 3, md: 6 },
                    height: '100%',
                    minHeight: 0
                }}
            >
                <Grid container spacing={0} sx={{ height: '100%', minHeight: 0 }}>

                    {/* LEFT COLUMN: METADATA & CONTROL TRACK (30%) */}
                    <Grid item xs={12} md={3} sx={{
                        borderRight: { md: '1px solid #000' },
                        pr: { md: 6 },
                        mb: { xs: 4, md: 0 },
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'space-between',
                        height: '100%',
                        minHeight: 0
                    }}>
                        <Box>
                            <Box>
                                <Typography variant="h2" sx={{
                                    fontFamily: '"JetBrains Mono", monospace',
                                    fontWeight: 700,
                                    fontSize: 'clamp(1.8rem, 4.8vmin, 3rem)',
                                    lineHeight: 0.8,
                                    letterSpacing: '-0.05em',
                                    mb: 'clamp(10px, 1.6vmin, 16px)'
                                }}>
                                    INTEL.<br />DIGITAL
                                </Typography>
                                <TechLabel color="#CC0000">SYS_ID: GNS-V2-A</TechLabel>
                            </Box>

                            <Divider sx={{ borderColor: '#000', my: 'clamp(16px, 3vmin, 32px)' }} />

                            {/* Capability Index */}
                            <Box sx={{ mb: 4 }}>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                                    <TechLabel>CAPABILITY INDEX</TechLabel>
                                    <TechLabel color="#000">98.4%</TechLabel>
                                </Box>
                                <Box sx={{ width: '100%', height: '4px', bgcolor: '#ddd' }}>
                                    <Box sx={{ width: '98.4%', height: '100%', bgcolor: '#7a7a7a' }} />
                                </Box>
                            </Box>

                            {/* Status Block */}
                            <Box
                                sx={{
                                    fontFamily: '"JetBrains Mono", monospace',
                                    fontSize: 'clamp(0.7rem, 1.2vmin, 0.78rem)',
                                    color: '#555',
                                    lineHeight: 1.6
                                }}
                            >
                                <p style={{ margin: 0 }}>STATUS: <span style={{ color: '#7a7a7a' }}>OPERATIONAL</span></p>
                                <p style={{ margin: 0 }}>MODE: TRANSLATION</p>
                                <p style={{ margin: 0 }}>TARGET: INSTITUTIONAL</p>
                            </Box>
                        </Box>

                        <Box sx={{ pt: 4, display: { xs: 'none', md: 'block' } }}>
                            <Typography variant="caption" sx={{ display: 'block', fontStyle: 'italic', color: '#888' }}>
                                "Não é sobre modismo. É sobre sistema, método e resultado."
                            </Typography>
                        </Box>
                    </Grid>

                    {/* RIGHT COLUMN: THE MANIFESTO (70%) */}
                    <Grid item xs={12} md={9} sx={{
                        pl: { md: 8 },
                        height: '100%',
                        minHeight: 0,
                        display: 'flex',
                        flexDirection: 'column',
                        // Avoid vertical centering; it causes top clipping when content is near the limit.
                        justifyContent: 'flex-start'
                    }}>

                        {/* Flex container for the 3 main blocks to distribute space evenly */}
                        <Box sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            // "space-around" can push content out on short screens.
                            // Use controlled gaps + scalable type instead.
                            justifyContent: 'flex-start',
                            gap: 'clamp(12px, 2.2vmin, 28px)',
                            height: '100%',
                            minHeight: 0
                        }}>

                            {/* MODULE 1: INPUT */}
                            <Box
                                onMouseEnter={() => setHoveredZone('input')}
                                onMouseLeave={() => setHoveredZone(null)}
                                sx={{ opacity: hoveredZone && hoveredZone !== 'input' ? 0.3 : 1, transition: 'opacity 0.3s' }}
                            >
                                <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: 2, mb: 1 }}>
                                    <Typography variant="h1" sx={{
                                        fontSize: 'clamp(2.0rem, 5.8vmin, 4.6rem)',
                                        fontWeight: 500,
                                        letterSpacing: '-0.03em',
                                        lineHeight: 0.9,
                                        fontFamily: "'Inter', sans-serif"
                                    }}>
                                        Tecnologia é <br />
                                        <span style={{ fontStyle: 'italic', fontFamily: "'Playfair Display', serif" }}>Linguagem.</span>
                                    </Typography>
                                    <Typography variant="h6" sx={{
                                        fontFamily: '"JetBrains Mono", monospace',
                                        writingMode: 'vertical-rl',
                                        transform: 'rotate(180deg)',
                                        color: '#CC0000',
                                        display: { xs: 'none', md: 'block' }
                                    }}>
                                        [INPUT]
                                    </Typography>
                                </Box>

                                <SchematicLine active={hoveredZone === 'input'} />

                                <Typography sx={{ maxWidth: '600px', fontSize: 'clamp(0.9rem, 1.55vmin, 1.05rem)', color: '#444' }}>
                                    O primeiro passo não é desenvolver software, é entender a natureza do problema, o propósito e onde se quer chegar.
                                </Typography>
                            </Box>

                            {/* MODULE 2: PROCESS */}
                            <Box
                                onMouseEnter={() => setHoveredZone('process')}
                                onMouseLeave={() => setHoveredZone(null)}
                                sx={{ ml: { md: 12 }, opacity: hoveredZone && hoveredZone !== 'process' ? 0.3 : 1, transition: 'opacity 0.3s' }}
                            >
                                <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: 2, mb: 1 }}>
                                    <Typography variant="h1" sx={{
                                        fontSize: 'clamp(2.0rem, 5.8vmin, 4.6rem)',
                                        fontWeight: 500,
                                        letterSpacing: '-0.03em',
                                        lineHeight: 0.9,
                                        fontFamily: "'Inter', sans-serif"
                                    }}>
                                        Transformação é <br />
                                        <span style={{ fontStyle: 'italic', fontFamily: "'Playfair Display', serif" }}>Leitura.</span>
                                    </Typography>
                                    <Typography variant="h6" sx={{
                                        fontFamily: '"JetBrains Mono", monospace',
                                        writingMode: 'vertical-rl',
                                        transform: 'rotate(180deg)',
                                        color: '#CC0000',
                                        display: { xs: 'none', md: 'block' }
                                    }}>
                                        [PROCESS]
                                    </Typography>
                                </Box>

                                <SchematicLine active={hoveredZone === 'process'} />

                                <Typography sx={{ maxWidth: '600px', fontSize: 'clamp(0.9rem, 1.55vmin, 1.05rem)', color: '#444' }}>
                                    Uma organização inteligente lê o contexto antes de agir.
                                    Traduzimos complexidade técnica em capacidade institucional de decidir e antecipar.
                                    Só então desenvolvemos ou aprimoramos tecnologias.
                                </Typography>
                            </Box>

                            {/* MODULE 3: OUTPUT */}
                            <Box
                                onMouseEnter={() => setHoveredZone('output')}
                                onMouseLeave={() => setHoveredZone(null)}
                                sx={{ ml: { md: 0 }, opacity: hoveredZone && hoveredZone !== 'output' ? 0.3 : 1, transition: 'opacity 0.3s' }}
                            >
                                <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: 2, mb: 1 }}>
                                    <Typography variant="h1" sx={{
                                        fontSize: 'clamp(2.1rem, 6.1vmin, 4.8rem)',
                                        fontWeight: 900,
                                        letterSpacing: '-0.03em',
                                        lineHeight: 0.9,
                                        fontFamily: "'Inter', sans-serif"
                                    }}>
                                        Inteligência <br />
                                        <span style={{ color: '#CC0000' }}>Digital.</span>
                                    </Typography>
                                    <Typography variant="h6" sx={{
                                        fontFamily: '"JetBrains Mono", monospace',
                                        writingMode: 'vertical-rl',
                                        transform: 'rotate(180deg)',
                                        color: '#CC0000',
                                        display: { xs: 'none', md: 'block' }
                                    }}>
                                        [OUTPUT]
                                    </Typography>
                                </Box>

                                <SchematicLine active={hoveredZone === 'output'} />

                                <Typography sx={{ maxWidth: '700px', fontSize: 'clamp(0.95rem, 1.75vmin, 1.15rem)', fontWeight: 500, color: '#000' }}>
                                    Inteligência Digital é a capacidade de gerar valor por meio da tecnologia.
                                    <br /><br />
                                    Não vendemos tecnologia pronta.<br />
                                    Construímos sua capacidade de performar por meio dela.
                                </Typography>
                            </Box>

                            {/* Signature Block - Kept inside flex container or bottom */}
                            <Box
                                sx={{
                                    pt: 2,
                                    borderTop: '2px solid #000',
                                    display: 'flex',
                                    gap: 4,
                                    mt: 1.5,
                                    // If viewport is too short, drop signature to preserve main typography fit.
                                    '@media (max-height: 760px)': { display: 'none' }
                                }}
                            >
                                <Box>
                                    <TechLabel>DATE</TechLabel>
                                    <Typography>FEB.2026</Typography>
                                </Box>
                            </Box>

                        </Box>
                    </Grid>
                </Grid>
            </Container>
        </Box>
    );
};

export default GenesisV2_Intel_SeedA;
